import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { speak, cancelSpeech } from "../lib/speech";
import { Waveform, ArrowRight, SpeakerHigh, Lock } from "@phosphor-icons/react";

export default function PublicDeckView() {
  const { deckId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/public/decks/${deckId}`)
      .then(({ data }) => setData(data))
      .catch(() => setErr("This deck is private or no longer available."));
    return () => cancelSpeech();
  }, [deckId]);

  if (err) {
    return (
      <div className="min-h-screen bg-[color:var(--paper)] flex flex-col">
        <PublicNav />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <div className="eyebrow mb-4">Unavailable</div>
            <h1 className="font-serif text-4xl">Deck not found</h1>
            <p className="text-[color:var(--ink-muted)] mt-4">{err}</p>
            <Link to="/" className="btn-primary mt-8 inline-flex">Explore Ekkol <ArrowRight size={16}/></Link>
          </div>
        </main>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[color:var(--paper)]"><PublicNav/>
        <div className="max-w-3xl mx-auto py-24 px-6"><div className="wave"><span/><span/><span/><span/><span/></div></div>
      </div>
    );
  }

  const { deck, cards } = data;
  const isFlashcard = deck.mode === "qa" && deck.qa_style === "flashcard";

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <PublicNav />
      <main className="max-w-4xl mx-auto px-6 md:px-10 py-12 fade-in">
        <div className="eyebrow mb-3">Shared deck · read-only preview</div>
        <h1 className="font-serif text-4xl sm:text-5xl leading-none tracking-tight" data-testid="public-deck-title">{deck.title}</h1>
        {deck.owner_name && <div className="mt-3 font-mono text-xs text-[color:var(--ink-muted)]">BY {deck.owner_name.toUpperCase()}</div>}
        {deck.description && <p className="mt-3 text-[color:var(--ink-muted)] max-w-2xl">{deck.description}</p>}

        <div className="mt-6 flex items-center gap-3">
          <div className="border rule bg-white px-4 py-2 text-xs font-mono uppercase tracking-[0.2em]">
            {deck.mode === "qa" ? (isFlashcard ? "Flashcards" : "Statements") : "Script"} · {deck.card_count} cards
          </div>
          <Link to="/login" className="btn-primary" data-testid="public-signin-cta">
            <Lock size={14}/> Sign in to practice hands-free
          </Link>
        </div>

        <div className="mt-10 border rule bg-white divide-y rule" data-testid="public-cards-list">
          {cards.map((c, i) => (
            <div key={c.card_id} className="p-5 md:p-6 flex gap-5 items-start">
              <div className="font-mono text-xs pt-2 w-8 text-[color:var(--ink-faint)]">{String(i+1).padStart(2,"0")}</div>
              <div className="flex-1">
                {isFlashcard && c.prompt ? (
                  <>
                    <p className="font-serif text-xl leading-snug">{c.prompt}</p>
                    <p className="mt-2 font-serif text-base text-[color:var(--ink-muted)]">
                      <span className="eyebrow mr-2">Answer</span>{c.content}
                    </p>
                  </>
                ) : (
                  <p className="font-serif text-xl leading-relaxed">{c.content}</p>
                )}
              </div>
              <button
                onClick={() => { cancelSpeech(); speak(c.prompt || c.content); }}
                className="text-[color:var(--ink-faint)] hover:text-[color:var(--oxford)] mt-2"
                aria-label="Preview voice"
                data-testid={`public-play-${i}`}
              >
                <SpeakerHigh size={18}/>
              </button>
            </div>
          ))}
        </div>

        <div className="mt-12 border rule bg-white p-8 md:p-10 text-center">
          <div className="eyebrow mb-3">Want the hands-free experience?</div>
          <h2 className="font-serif text-3xl">Practice this deck with your voice.</h2>
          <p className="text-[color:var(--ink-muted)] mt-3 max-w-xl mx-auto">
            Sign in free to rehearse hands-free: Ekkol reads each card, listens while you repeat,
            and scores every attempt.
          </p>
          <div className="mt-6">
            <Link to="/login" className="btn-primary" data-testid="public-bottom-cta">
              Start for free <ArrowRight size={16} weight="bold"/>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

function PublicNav() {
  return (
    <nav className="w-full border-b rule bg-white/80 backdrop-blur-xl sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Waveform size={22} weight="duotone" />
          <span className="font-serif text-2xl leading-none">Ekkol</span>
        </Link>
        <Link to="/login" className="btn-primary py-2 px-4 text-sm">Sign in</Link>
      </div>
    </nav>
  );
}
